import { useEffect, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONVERSATIONS } from "@/constants/testIds";

// Fix AC (2026-10-15): crear o editar una plantilla de WhatsApp. Los datos que
// cambian en cada envio se insertan con los botones (nombre, tratamiento...).
export const VARIABLE_LABELS = { nombre: "Nombre del cliente", tratamiento: "Tratamiento", fecha: "Fecha", hora: "Hora" };
const EXAMPLES = { nombre: "María", tratamiento: "Botox", fecha: "20 de octubre", hora: "10:30" };

export function templateName(title) {
  return (title || "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "").slice(0, 60);
}

export function previewText(body, footer) {
  const text = (body || "").replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (_, k) => EXAMPLES[k] || `[${k}]`);
  return [text, footer].filter(Boolean).join("\n\n");
}

export function labelText(body, footer) {
  const text = (body || "").replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_, k) => `[${VARIABLE_LABELS[k] || k}]`);
  return [text, footer].filter(Boolean).join("\n\n");
}

export default function TemplateFormDialog({ open, onOpenChange, template, onSubmit }) {
  const editing = Boolean(template);
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("es");
  const [body, setBody] = useState("");
  const [footer, setFooter] = useState("");
  const [buttons, setButtons] = useState([]);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTitle(template ? template.name.replaceAll("_", " ") : "");
    setLanguage(template?.language || "es");
    setBody(template?.body || "");
    setFooter(template?.footer || "");
    setButtons(template?.buttons || []);
    setSaving(false);
  }, [open, template]);

  const insert = (key) => {
    const el = bodyRef.current;
    const tag = `{{${key}}}`;
    const start = el ? el.selectionStart : body.length;
    const end = el ? el.selectionEnd : body.length;
    const next = body.slice(0, start) + tag + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(start + tag.length, start + tag.length); } });
  };

  const name = editing ? template.name : templateName(title);
  const canSave = !saving && body.trim() && (editing || name);

  const submit = async () => {
    setSaving(true);
    const ok = await onSubmit({ title, language, body, footer, buttons: buttons.filter((b) => b.trim()) });
    if (!ok) setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={CONVERSATIONS.templateFormDialog}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{editing ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {editing ? (
            <p className="text-sm text-muted-foreground">{template.name.replaceAll("_", " ")} ({template.language}). Al guardar, Meta la vuelve a revisar.</p>
          ) : (
            <>
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Título</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Seguimiento Botox" data-testid={CONVERSATIONS.templateTitleInput} />
                {name && <p className="text-xs text-muted-foreground mt-1">Nombre en WhatsApp: {name}</p>}
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Idioma</Label>
                <select value={language} onChange={(e) => setLanguage(e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid={CONVERSATIONS.templateLanguageSelect}>
                  <option value="es">Español</option>
                  <option value="en">Inglés</option>
                </select>
              </div>
            </>
          )}
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Mensaje</Label>
            <Textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={1024}
              placeholder="Hola {{nombre}}, te escribimos de Strani Just Because para saber cómo te fue con tu tratamiento de {{tratamiento}}."
              data-testid={CONVERSATIONS.templateBodyInput} />
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-xs text-muted-foreground self-center">Agregar dato:</span>
              {Object.entries(VARIABLE_LABELS).map(([key, label]) => (
                <Button key={key} type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => insert(key)} data-testid={`${CONVERSATIONS.templateVariablePrefix}${key}`}>
                  {label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Nombre y tratamiento se completan solos al mandarla. El mensaje no puede empezar ni terminar con un dato.</p>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Pie (opcional)</Label>
            <Input value={footer} onChange={(e) => setFooter(e.target.value)} maxLength={60} placeholder="Strani Just Because · Houston" data-testid={CONVERSATIONS.templateFooterInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Botones de respuesta (opcional, hasta 3)</Label>
            <div className="space-y-2">
              {buttons.map((b, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={b} maxLength={25} onChange={(e) => setButtons((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))} placeholder="Quiero agendar" data-testid={`${CONVERSATIONS.templateButtonInputPrefix}${i}`} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => setButtons((prev) => prev.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
                </div>
              ))}
              {buttons.length < 3 && (
                <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setButtons((prev) => [...prev, ""])} data-testid={CONVERSATIONS.templateAddButton}>
                  <Plus className="w-3.5 h-3.5" /> Agregar botón
                </Button>
              )}
            </div>
          </div>
          {body.trim() && (
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Así le llega (con datos de ejemplo)</Label>
              <p className="text-sm whitespace-pre-wrap bg-muted rounded-lg px-3 py-2" data-testid={CONVERSATIONS.templateFormPreview}>{previewText(body, footer)}</p>
              {buttons.filter((b) => b.trim()).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">{buttons.filter((b) => b.trim()).map((b, i) => <span key={i} className="text-xs border border-border rounded-full px-2.5 py-1">{b}</span>)}</div>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground">Meta revisa cada plantilla antes de que se pueda usar; suele tardar de minutos a unas horas. Se crea como mensaje de seguimiento (Utilidad): no uses tono de promoción, porque Meta no entrega promociones a números de Estados Unidos.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={!canSave} className="bg-brand hover:bg-brand-hover text-white" data-testid={CONVERSATIONS.templateSaveButton}>
            {saving ? "Enviando..." : "Enviar a aprobar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
